import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getDefaultConsent,
  getStoredConsent,
  restoreConsentFromStorage,
  saveConsent,
} from '../utils/cookieConsent';

const CookieConsentContext = createContext(null);

function isClientConsentReady() {
  if (typeof navigator === 'undefined') return false;
  // react-snap prerenders with this UA; banner must not be baked into static HTML.
  if (navigator.userAgent.includes('ReactSnap')) return false;
  return true;
}

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(() => getStoredConsent() || getDefaultConsent());
  const [hasDecided, setHasDecided] = useState(() => Boolean(getStoredConsent()));
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [draftConsent, setDraftConsent] = useState(() => getStoredConsent() || getDefaultConsent());
  const [clientReady, setClientReady] = useState(false);

  useEffect(() => {
    if (!isClientConsentReady()) return;
    setClientReady(true);
    restoreConsentFromStorage();
  }, []);

  const persistConsent = useCallback((nextConsent) => {
    const saved = saveConsent(nextConsent);
    setConsent(saved);
    setDraftConsent(saved);
    setHasDecided(true);
    setIsPreferencesOpen(false);
    return saved;
  }, []);

  const acceptAll = useCallback(() => {
    persistConsent({ essential: true, analytics: true, marketing: true });
  }, [persistConsent]);

  const rejectNonEssential = useCallback(() => {
    persistConsent({ essential: true, analytics: false, marketing: false });
  }, [persistConsent]);

  const savePreferences = useCallback(() => {
    persistConsent({
      essential: true,
      analytics: draftConsent.analytics,
      marketing: draftConsent.marketing,
    });
  }, [draftConsent.analytics, draftConsent.marketing, persistConsent]);

  const openPreferences = useCallback(() => {
    setDraftConsent(consent);
    setIsPreferencesOpen(true);
  }, [consent]);

  const closePreferences = useCallback(() => {
    setIsPreferencesOpen(false);
    setDraftConsent(consent);
  }, [consent]);

  const updateDraftConsent = useCallback((category, value) => {
    setDraftConsent((prev) => ({
      ...prev,
      [category]: value,
    }));
  }, []);

  const value = useMemo(
    () => ({
      consent,
      draftConsent,
      hasDecided,
      isPreferencesOpen,
      isBannerVisible: clientReady && !hasDecided,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openPreferences,
      closePreferences,
      updateDraftConsent,
    }),
    [
      acceptAll,
      closePreferences,
      consent,
      draftConsent,
      clientReady,
      hasDecided,
      isPreferencesOpen,
      openPreferences,
      rejectNonEssential,
      savePreferences,
      updateDraftConsent,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};
